import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, Menu, Search, X } from "lucide-react";
import BrandMark from "./BrandMark";

const navItems = [
  { to: "/dashboard", label: "Scanner" },
  { to: "/community", label: "Community" },
  { to: "/watchlists", label: "Watchlists" },
  { to: "/alerts", label: "Alerts" },
  { to: "/backtester", label: "Backtester" },
  { to: "/pricing", label: "Pricing" },
  { to: "/support", label: "Support" },
];

export default function Header({ onSearch, searchQuery, setSearchQuery }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to="/" className="shrink-0" data-testid="brand-link">
          <BrandMark size={36} />
        </Link>

        {onSearch ? (
          <div className="hidden md:flex flex-1 max-w-xl mx-auto" data-testid="global-search">
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery && setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch(searchQuery)}
                placeholder="Search ticker, contract, or setup…"
                aria-label="Search ticker, contract, or setup"
                className="input-glass pl-10 pr-10"
                data-testid="global-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery && setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ) : <div className="flex-1" />}

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-[13px] font-medium tracking-tight transition-colors rounded-lg ${
                  isActive ? "text-white bg-white/[0.06]" : "text-white/55 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {user && (
            <NavLink
              to="/profile"
              data-testid="nav-profile"
              className={({ isActive }) =>
                `px-3 py-1.5 text-[13px] font-medium tracking-tight transition-colors rounded-lg ${
                  isActive ? "text-white bg-white/[0.06]" : "text-white/55 hover:text-white"
                }`
              }
            >
              Account
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `px-3 py-1.5 text-[13px] font-medium tracking-tight transition-colors rounded-lg ${
                  isActive
                    ? "text-white bg-[var(--bull)]/15 border border-[var(--bull)]/40"
                    : "text-[var(--bull)]/85 hover:text-[var(--bull)]"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 border border-white/10 rounded-full bg-white/[0.04] backdrop-blur-md" data-testid="user-plan-badge">
                <span className="label-mono">PLAN</span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-white">
                  {user.plan}
                </span>
              </span>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="btn-ghost hidden sm:inline-flex items-center gap-1.5"
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
              <button onClick={() => setOpen(!open)} className="md:hidden btn-ghost p-2.5" data-testid="menu-toggle">
                <Menu className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="header-login-btn">Log in</Link>
              <Link to="/register" className="btn-primary" data-testid="header-signup-btn">Get started</Link>
            </>
          )}
        </div>
      </div>

      {open && user && (
        <div className="md:hidden border-t border-white/5 px-4 py-3 flex flex-col gap-2 bg-black/60 backdrop-blur-xl">
          {navItems.map((n) => (
            <Link key={n.to} to={n.to} className="text-white/80 text-sm py-1" onClick={() => setOpen(false)}>{n.label}</Link>
          ))}
          <Link to="/profile" className="text-white/80 text-sm py-1" onClick={() => setOpen(false)}>Account</Link>
          <button className="btn-ghost mt-2" onClick={async () => { await logout(); navigate("/"); }}>Logout</button>
        </div>
      )}
    </header>
  );
}
